import { TokenStorage } from '@/config/storage';

// Eventos para sincronização entre abas
const TOKEN_SYNC_EVENTS = {
  TOKEN_REFRESHED: 'multimarcas:token-refreshed',
  TOKEN_EXPIRED: 'multimarcas:token-expired',
  SESSION_CLEARED: 'multimarcas:session-cleared',
} as const;

interface TokenSyncData {
  accessToken: string;
  refreshToken: string;
  timestamp: number;
}

class TokenSyncService {
  private isInitialized = false;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();

  // Inicializa o serviço de sincronização
  init() {
    if (this.isInitialized) {
      return;
    }

    // Listener para eventos de sincronização
    window.addEventListener('storage', this.handleStorageChange.bind(this));

    // Listener para eventos de broadcast
    window.addEventListener('message', this.handleMessage.bind(this));

    this.isInitialized = true;
  }

  // Limpa o serviço
  destroy() {
    if (!this.isInitialized) {
      return;
    }

    window.removeEventListener('storage', this.handleStorageChange.bind(this));
    window.removeEventListener('message', this.handleMessage.bind(this));

    this.listeners.clear();
    this.isInitialized = false;
    console.log('[TokenSync] Serviço destruído');
  }

  // Notifica outras abas sobre renovação de token
  notifyTokenRefreshed(accessToken: string, refreshToken: string) {
    const data: TokenSyncData = {
      accessToken,
      refreshToken,
      timestamp: Date.now(),
    };

    // Salva no localStorage para sincronização
    try {
      localStorage.setItem('multimarcas:token-sync', JSON.stringify(data));
    } catch (error) {
      console.warn('[TokenSync] Erro ao salvar token no localStorage:', error);
    }

    // Envia mensagem para outras abas
    this.broadcastMessage(TOKEN_SYNC_EVENTS.TOKEN_REFRESHED, data);

    console.log('[TokenSync] Token renovado notificado para outras abas');
  }

  // Notifica outras abas sobre expiração de token
  notifyTokenExpired() {
    this.broadcastMessage(TOKEN_SYNC_EVENTS.TOKEN_EXPIRED, { timestamp: Date.now() });
    console.log('[TokenSync] Expiração de token notificada para outras abas');
  }

  // Notifica outras abas sobre limpeza de sessão
  notifySessionCleared() {
    this.broadcastMessage(TOKEN_SYNC_EVENTS.SESSION_CLEARED, { timestamp: Date.now() });
    console.log('[TokenSync] Limpeza de sessão notificada para outras abas');
  }

  // Adiciona listener para eventos de sincronização
  addListener(event: string, callback: (data: any) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  // Remove listener
  removeListener(event: string, callback: (data: any) => void) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
    }
  }

  // Envia mensagem para outras abas
  private broadcastMessage(event: string, data: any) {
    try {
      // Envia para todas as abas da mesma origem
      window.postMessage({
        type: event,
        data,
        source: 'multimarcas-token-sync',
      }, window.location.origin);
    } catch (error) {
      console.warn('[TokenSync] Erro ao enviar mensagem:', error);
    }
  }

  // Manipula mudanças no localStorage
  private handleStorageChange(event: StorageEvent) {
    if (event.key === 'multimarcas:token-sync' && event.newValue) {
      try {
        const data: TokenSyncData = JSON.parse(event.newValue);

        // Atualiza os tokens locais
        TokenStorage.setTokens(data.accessToken, data.refreshToken);

        // Notifica listeners
        this.notifyListeners(TOKEN_SYNC_EVENTS.TOKEN_REFRESHED, data);

        console.log('[TokenSync] Token sincronizado via localStorage');
      } catch (error) {
        console.error('[TokenSync] Erro ao processar sincronização:', error);
      }
    }
  }

  // Manipula mensagens de outras abas
  private handleMessage(event: MessageEvent) {
    if (
      event.origin !== window.location.origin ||
      event.data?.source !== 'multimarcas-token-sync'
    ) {
      return;
    }

    const { type, data } = event.data;

    switch (type) {
      case TOKEN_SYNC_EVENTS.TOKEN_REFRESHED:
        // Atualiza os tokens locais
        TokenStorage.setTokens(data.accessToken, data.refreshToken);
        this.notifyListeners(type, data);
        console.log('[TokenSync] Token sincronizado via postMessage');
        break;

      case TOKEN_SYNC_EVENTS.TOKEN_EXPIRED:
        this.notifyListeners(type, data);
        console.log('[TokenSync] Expiração de token sincronizada');
        break;

      case TOKEN_SYNC_EVENTS.SESSION_CLEARED:
        // Não limpa a sessão local, apenas notifica os listeners
        // A sessão será mantida até que o usuário faça logout manualmente
        this.notifyListeners(type, data);
        console.log('[TokenSync] Notificação de limpeza de sessão recebida - mantendo conexão local');
        break;
    }
  }

  // Notifica todos os listeners de um evento
  private notifyListeners(event: string, data: any) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('[TokenSync] Erro no listener:', error);
        }
      });
    }
  }
}

// Instância global do serviço
export const tokenSyncService = new TokenSyncService();

// Inicializa automaticamente quando o módulo é carregado
if (typeof window !== 'undefined') {
  tokenSyncService.init();
} 